/**
 * Store Locator Shared Utilities
 * Common functionality used by store-locator.js
 */

(function() {
  'use strict';

  /**
   * Constants for store locator functionality
   */
  const CONSTANTS = {
    EARTH_RADIUS_KM: 6371,
    MAX_SEARCHES_PER_MINUTE: 30,
    MIN_SEARCH_INTERVAL_MS: 100,
    DETAIL_ZOOM_LEVEL: 15,
    GEOCODE_DEBOUNCE_MS: 800
  };

  /**
   * Cached element for HTML escaping (performance optimization)
   */
  const escapeDiv = document.createElement('div');

  /**
   * Convert degrees to radians
   * @param {number} deg - Degrees to convert
   * @returns {number} Radians
   */
  function toRad(deg) {
    return deg * (Math.PI / 180);
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @param {number} lat1 - Latitude of first point
   * @param {number} lon1 - Longitude of first point
   * @param {number} lat2 - Latitude of second point
   * @param {number} lon2 - Longitude of second point
   * @returns {number} Distance in kilometers
   */
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = CONSTANTS.EARTH_RADIUS_KM;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Calculate distance between two point objects
   * @param {{lat: number, lng: number}} p1 - First point
   * @param {{lat: number, lng: number}} p2 - Second point
   * @returns {number} Distance in kilometers
   */
  function calculateDistanceFromPoints(p1, p2) {
    return calculateDistance(p1.lat, p1.lng, p2.lat, p2.lng);
  }

  /**
   * Format distance for display
   * @param {number} km - Distance in kilometers
   * @returns {string} Formatted distance string
   */
  function formatDistance(km) {
    if (km < 1) {
      return Math.round(km * 1000) + ' m';
    }
    return km.toFixed(1) + ' km';
  }

  /**
   * Escape HTML to prevent XSS
   * Uses cached element for better performance
   * @param {string|object} text - Text to escape
   * @returns {string} Escaped HTML string
   */
  function escapeHtml(text) {
    if (!text) return '';
    if (typeof text === 'object') {
      text = text.value || text.toString();
    }
    escapeDiv.textContent = String(text);
    return escapeDiv.innerHTML;
  }

  /**
   * Filter stores by text query
   * @param {Array} stores - Array of store objects
   * @param {string} query - Search query
   * @returns {Array} Filtered stores
   */
  function filterStoresByQuery(stores, query) {
    const searchQuery = query.toLowerCase().trim();
    if (!searchQuery) return stores;

    return stores.filter(function(store) {
      return (
        (store.name && store.name.toLowerCase().includes(searchQuery)) ||
        (store.city && store.city.toLowerCase().includes(searchQuery)) ||
        (store.country && store.country.toLowerCase().includes(searchQuery)) ||
        (store.address && store.address.toLowerCase().includes(searchQuery))
      );
    });
  }

  /**
   * Sort stores by distance from a reference point
   * @param {Array} stores - Array of store objects
   * @param {number} lat - Reference latitude
   * @param {number} lng - Reference longitude
   * @returns {Array} Stores sorted by distance with distance property added
   */
  function sortStoresByDistance(stores, lat, lng) {
    return stores.map(function(store) {
      const distance = (store.lat && store.lng)
        ? calculateDistance(lat, lng, parseFloat(store.lat), parseFloat(store.lng))
        : Infinity;
      return Object.assign({}, store, { distance: distance });
    }).sort(function(a, b) {
      return a.distance - b.distance;
    });
  }

  /**
   * Animate a value change using requestAnimationFrame
   * @param {number} from - Starting value
   * @param {number} to - Ending value
   * @param {number} duration - Animation duration in ms
   * @param {function} onUpdate - Callback with current value
   * @param {function} [onComplete] - Callback when animation completes
   */
  function animateValue(from, to, duration, onUpdate, onComplete) {
    const startTime = performance.now();

    function tick(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * (2 - progress);
      const current = from + (to - from) * eased;

      onUpdate(current);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else if (onComplete) {
        onComplete();
      }
    }

    requestAnimationFrame(tick);
  }

  /**
   * Create a rate limiter for API calls
   * @param {number} maxPerMinute - Maximum calls per minute
   * @param {number} minInterval - Minimum interval between calls in ms
   * @returns {object} Rate limiter with check method
   */
  function createRateLimiter(maxPerMinute, minInterval) {
    let count = 0;
    let startTime = Date.now();
    let lastCallTime = 0;
    let userInteracted = false;

    return {
      markUserInteraction: function() {
        userInteracted = true;
      },

      isLimited: function() {
        const now = Date.now();
        const timeSinceStart = now - startTime;

        if (timeSinceStart > 60000) {
          count = 0;
          startTime = now;
        }

        if (count >= maxPerMinute) {
          return true;
        }

        if (now - lastCallTime < minInterval) {
          return true;
        }

        if (!userInteracted) {
          return true;
        }

        count++;
        lastCallTime = now;
        return false;
      }
    };
  }

  /**
   * Parse opening hours data that may arrive as a JSON string or an object.
   * @param {object|string} hours - Opening hours payload
   * @returns {object|null} Parsed hours map keyed by weekday, or null when invalid
   */
  function parseOpeningHours(hours) {
    if (!hours) return null;
    let parsed = hours;

    if (typeof hours === 'string') {
      try {
        parsed = JSON.parse(hours);
      } catch (e) {
        return null;
      }
    }

    return (parsed && typeof parsed === 'object') ? parsed : null;
  }

  /**
   * Determine whether a store is currently open based on its opening hours.
   * Uses the visitor's local clock; intended for display-only badges.
   *
   * Expected shape: { monday: { open: 'HH:MM', close: 'HH:MM', closed: bool }, ... }
   *
   * @param {object|string} hours - Opening hours payload
   * @param {Date} [now] - Reference time (defaults to new Date())
   * @returns {boolean|null} true/false, or null when the hours are unknown/invalid
   */
  function isOpenNow(hours, now) {
    const parsed = parseOpeningHours(hours);
    if (!parsed) return null;

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const reference = now instanceof Date ? now : new Date();
    const dayIndex = reference.getDay() === 0 ? 6 : reference.getDay() - 1;
    const today = parsed[days[dayIndex]];

    if (!today || today.closed || !today.open || !today.close) return false;

    const minutes = reference.getHours() * 60 + reference.getMinutes();
    const open = parseTimeToMinutes(today.open);
    const close = parseTimeToMinutes(today.close);
    if (open === null || close === null) return false;

    // Support closing times past midnight (e.g. "00:00" or "02:00")
    if (close <= open) {
      return minutes >= open || minutes < close;
    }

    return minutes >= open && minutes < close;
  }

  /**
   * Convert a 'HH:MM' string to minutes-since-midnight.
   * @param {string} timeStr
   * @returns {number|null}
   */
  function parseTimeToMinutes(timeStr) {
    if (typeof timeStr !== 'string') return null;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  window.StoreLocatorUtils = {
    CONSTANTS: CONSTANTS,
    calculateDistance: calculateDistance,
    calculateDistanceFromPoints: calculateDistanceFromPoints,
    formatDistance: formatDistance,
    escapeHtml: escapeHtml,
    filterStoresByQuery: filterStoresByQuery,
    sortStoresByDistance: sortStoresByDistance,
    animateValue: animateValue,
    createRateLimiter: createRateLimiter,
    parseOpeningHours: parseOpeningHours,
    isOpenNow: isOpenNow
  };
})();
